import { ReactElement, ReactNode, useState } from 'react';

// material-ui
import { Box, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// project imports
import useConfig from '@/hooks/useConfig';

// ==============================|| MINI SIDEBAR LABEL POPUP ||============================== //

interface MiniNavTooltipProps {
    title?: ReactNode;
    caption?: ReactNode;
    children: ReactElement;
    /** Hide the label while a submenu flyout is open. */
    forceClose?: boolean;
}

const MiniNavTooltip = ({ title, caption, children, forceClose = false }: MiniNavTooltipProps) => {
    const theme = useTheme();
    const { borderRadius } = useConfig();
    const [hoverOpen, setHoverOpen] = useState(false);

    if (!title && !caption) {
        return children;
    }

    return (
        <Tooltip
            placement="right"
            arrow
            enterDelay={200}
            open={!forceClose && hoverOpen}
            onOpen={() => setHoverOpen(true)}
            onClose={() => setHoverOpen(false)}
            disableHoverListener={forceClose}
            disableFocusListener={forceClose}
            disableTouchListener={forceClose}
            title={
                <Box>
                    {title ? (
                        <Typography variant="body1" sx={{ display: 'block', fontWeight: 600, lineHeight: 1.3, color: 'text.primary' }}>
                            {title}
                        </Typography>
                    ) : null}
                    {caption ? (
                        <Typography
                            variant="caption"
                            sx={{
                                ...theme.typography.subMenuCaption,
                                display: 'block',
                                mt: title ? 0.25 : 0,
                                lineHeight: 1.35,
                                color: 'text.secondary'
                            }}
                        >
                            {caption}
                        </Typography>
                    ) : null}
                </Box>
            }
            slotProps={{
                popper: {
                    sx: { zIndex: 1500 },
                    modifiers: [{ name: 'offset', options: { offset: [0, 0] } }]
                },
                tooltip: {
                    sx: {
                        '&&': {
                            bgcolor: theme.palette.background.paper,
                            backgroundImage: 'none',
                            color: theme.palette.text.primary,
                            boxShadow: theme.customShadows?.z8 ?? theme.shadows[8],
                            borderRadius: `${borderRadius}px`,
                            px: 1.5,
                            py: 1,
                            maxWidth: 260
                        }
                    }
                },
                arrow: {
                    sx: {
                        color: theme.palette.background.paper
                    }
                }
            }}
        >
            {children}
        </Tooltip>
    );
};

export default MiniNavTooltip;
